export interface ChatModeData {
  mode: "casa" | "gladly";
  email: string;
  phone: string;
  name: string;
}

export interface GladlyUser {
  name?: string;
  email?: string;
  phoneNumber?: string;
}

interface WaitForGladlyReadyOptions {
  interval?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
}

interface StartGladlyConversationOptions {
  historyText?: string;
}

interface HandoffToGladlyOptions {
  user?: GladlyUser;
  historyText?: string;
  signal?: AbortSignal;
  onBeforeOpen?: () => void;
}

const GLADLY_MARKET = "us";
const DEFAULT_WAIT_INTERVAL = 1000;
const DEFAULT_MAX_ATTEMPTS = 15;
const GLADLY_NOT_AVAILABLE_MESSAGE = "Gladly is not available";
const GLADLY_INIT_TIMEOUT_MESSAGE = "Gladly SDK initialization timeout";

export const GLADLY_REFRESH_MESSAGE =
  "Gladly failed to initialize. Please refresh the page and try again.";

type GladlySdk = NonNullable<
  NonNullable<Window["__CASA_CONFIG__"]>["features"]
>["gladly"] extends infer T ? NonNullable<T> : never;

export function hasGladlyFeature() {
  return Boolean(window.__CASA_CONFIG__?.features?.gladly);
}

export function getGladly(): GladlySdk | null {
  return window.__CASA_CONFIG__?.features?.gladly ?? null;
}

export function shouldCheckChatMode() {
  const currentMarket = window.__CASA_CONFIG__?.currentMarket?.toLowerCase();

  // 站内场景只有 US market 需要做 mode 检测；外部集成则只有接入 gladly 时才需要检测。
  return currentMarket === GLADLY_MARKET || hasGladlyFeature();
}

function isGladlyReady() {
  return typeof (window as any).Gladly?.show === "function";
}

async function getReadyGladlyOrThrow(
  options: WaitForGladlyReadyOptions = {},
): Promise<void> {
  const {
    interval = DEFAULT_WAIT_INTERVAL,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    signal,
  } = options;

  if (signal?.aborted) {
    throw new DOMException("Gladly readiness check aborted", "AbortError");
  }

  if (isGladlyReady()) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      if (signal?.aborted) {
        window.clearInterval(intervalId);
        reject(new DOMException("Gladly readiness check aborted", "AbortError"));
        return;
      }

      if (isGladlyReady()) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        reject(new Error(GLADLY_INIT_TIMEOUT_MESSAGE));
      }
    }, interval);
  });
}

function getGladlyOrThrow(): GladlySdk {
  const gladly = getGladly();
  if (!gladly) {
    throw new Error(GLADLY_NOT_AVAILABLE_MESSAGE);
  }

  return gladly;
}

export async function waitForGladlyReady(
  options?: WaitForGladlyReadyOptions,
) {
  await getReadyGladlyOrThrow(options);
}

export async function openGladly() {
  await getReadyGladlyOrThrow();
  const gladly = getGladlyOrThrow();
  gladly.show();
}

export async function closeGladly() {
  await getReadyGladlyOrThrow();
  const gladly = getGladlyOrThrow();
  gladly.close();
}

function getNormalizedGladlyUser(user?: GladlyUser) {
  if (!user) {
    return null;
  }

  const normalizedUser: GladlyUser = {};

  if (user.name) {
    normalizedUser.name = user.name;
  }

  if (user.email) {
    normalizedUser.email = user.email;
  } else if (user.phoneNumber) {
    normalizedUser.phoneNumber = user.phoneNumber;
  }

  return Object.keys(normalizedUser).length > 0 ? normalizedUser : null;
}

export async function syncGladlyUser(user?: GladlyUser) {
  const normalizedUser = getNormalizedGladlyUser(user);
  if (!normalizedUser) {
    return;
  }

  await getReadyGladlyOrThrow();
  const gladly = getGladlyOrThrow();
  await gladly.clearUser();
  gladly.setUser(normalizedUser);
}

export async function startGladlyConversation(
  options: StartGladlyConversationOptions = {},
) {
  const historyText = options.historyText?.trim();
  if (!historyText) {
    return;
  }

  await getReadyGladlyOrThrow();
  const gladly = getGladlyOrThrow();
  gladly.startConversation([
    {
      type: "TEXT",
      text: historyText,
    },
  ]);
}

export async function handoffToGladly(
  options: HandoffToGladlyOptions = {},
) {
  await getReadyGladlyOrThrow({ signal: options.signal });
  const gladly = getGladlyOrThrow();
  const normalizedUser = getNormalizedGladlyUser(options.user);

  options.onBeforeOpen?.();

  if (normalizedUser) {
    await gladly.clearUser();
    gladly.setUser(normalizedUser);
  }

  await startGladlyConversation({ historyText: options.historyText });

  return true;
}

export function shouldPromptGladlyRefresh(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.message === GLADLY_NOT_AVAILABLE_MESSAGE ||
    error.message === GLADLY_INIT_TIMEOUT_MESSAGE
  );
}
